import { DataValidationSheet, DataValidationSheetRef } from "@/components/DataValidationSheet";
import { ExcelExportButton } from "@/components/ExcelExportButton";
import { ExtractedDataCell } from "@/components/ExtractedDataCell";
import { StudyNameCell } from "@/components/StudyNameCell";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "./ui/card";
import { useDocumentViewer } from "@/contexts/DocumentViewerContext";
import { useProjectDetailContext } from "@/contexts/ProjectDetailContext";
import { toSafeFileName } from "@/hooks/useExcelExport";
import { usePicoExportRows } from "@/hooks/usePicoExportRows";
import { useTemplateDocuments, useUpdateTemplateDocument } from "@/hooks/useProjects";
import { useTemplateTypeSettings } from "@/hooks/useMasterData";
import { buildEvidenceSelectionDocIds, scopeDocumentsToSelection, withoutPendingSelections } from "@/lib/extractionSelection";
import { useAllActiveProjectTemplatesWithPrompts } from "@/hooks/useTemplates";
import TemplateTabStrip from "./TemplateTabStrip";
import { projectsApi } from "@/services/projectsApi";
import { auditLogApi } from "@/services/auditLogApi";
import "@/lib/ag-grid-setup";
import { Document, PicoExtraction, PicoSubgroup, TemplateDocumentData } from "@/types/models";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { CellClickedEvent, ColDef, FilterChangedEvent, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { FileText } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ResetFiltersButton from "@/components/ResetFiltersButton";
import type { MentionProvider } from "@lumanity/ui/chat";

const qaChecklist = [
  "All evidence documents reviewed and validated",
  "PICO extraction complete for all studies",
  "Citations properly formatted and linked",
  "Client review completed",
  "Final deliverables approved",
];

interface FieldDataWithAI {
  value: string;
  status: string;
  aiScore?: number;
  aiSuggestions?: string[];
  aiAlternativeAnswer?: string;
  /** @deprecated Legacy shape — only used for reading previously-persisted data */
  aiAlternativeAnswers?: Record<string, string>;
}

interface QARow {
  id: string;
  studyName: string;
  authors?: string;
  countries?: string;
  year?: string;
  sourceOrganisation?: string;
  filename?: string;
  studyId?: string;
  status?: string;
  fields?: Record<string, string>;
  fieldsData?: Record<string, FieldDataWithAI>;
  isParent: boolean;
  isExpanded?: boolean;
  parentId?: string;
  documentDataId?: string;
}

interface GridContext {
  toggleExpand: (id: string) => void;
  documents: Document[];
  onViewDocument: (doc: Document, page?: string, sourceText?: string) => void;
}

interface SelectedRow {
  studyName: string;
  fields: Record<string, string>;
  fieldsData: Record<string, FieldDataWithAI>;
  rowId: string;
  documentId: string;
  sourcefile: string;
  documentDataId?: string;
  subgroupIndex: number;
}


const parseSubgroupFromRecord = (
  record: TemplateDocumentData,
  documentId: string
): { index: number; documentDataId: string; subgroup: PicoSubgroup } | null => {
  if (!record.data) return null;

  try {
    const parsed = JSON.parse(record.data);
    return {
      index: parsed.index ?? 0,
      documentDataId: record.id,
      subgroup: {
        population: parsed.population,
        intervention: parsed.intervention,
        comparator: parsed.comparator,
        outcome: parsed.outcome,
        fields: parsed.fields,
      },
    };
  } catch {
    return null;
  }
};

const processFieldValue = (
  fieldValue: unknown,
  key: string
): { displayValue: string; fieldData: FieldDataWithAI } => {
  if (typeof fieldValue === "string") {
    return {
      displayValue: fieldValue,
      fieldData: { value: fieldValue, status: "Pending" }
    };
  }

  if (fieldValue && typeof fieldValue === "object" && "value" in fieldValue) {
    const field = fieldValue as FieldDataWithAI;
    // Resolve alternative answer: prefer canonical singular, fall back to legacy plural Record
    const resolvedAlternativeAnswer =
      field.aiAlternativeAnswer ?? field.aiAlternativeAnswers?.[key] ?? undefined;
    return {
      displayValue: field.value,
      fieldData: {
        value: field.value,
        status: field.status || "Pending",
        ...(field.aiScore !== undefined && { aiScore: field.aiScore }),
        ...(field.aiSuggestions && { aiSuggestions: field.aiSuggestions }),
        ...(resolvedAlternativeAnswer && { aiAlternativeAnswer: resolvedAlternativeAnswer }),
      }
    };
  }

  return {
    displayValue: "",
    fieldData: { value: "", status: "Pending" }
  };
};

const updateFieldStatus = (
  fieldData: FieldDataWithAI,
  approvedFieldName: string | undefined,
  fieldKey: string,
  markAllAsReviewed: boolean,
  markAllAsPending: boolean
): string => {
  if (markAllAsPending) return "Pending";
  if (approvedFieldName && approvedFieldName === `~${fieldKey}`) return "Pending";
  if (markAllAsReviewed || (approvedFieldName && fieldKey === approvedFieldName)) return "Reviewed";
  return fieldData.status;
};

const computeStudyStatus = (subgroups: PicoSubgroup[]): string => {
  if (subgroups.length === 0) return "Pending Extraction";

  const allSubgroupsReviewed = subgroups.every((sg) => {
    if (!sg.fields) return false;

    return Object.values(sg.fields).every((fieldValue) => {
      if (typeof fieldValue === "string") return false;
      if (fieldValue && typeof fieldValue === "object" && "status" in fieldValue) {
        const field = fieldValue as FieldDataWithAI;
        return field.status === "Reviewed" || field.status === "Approved";
      }
      return false;
    });
  });

  return allSubgroupsReviewed ? "Reviewed" : "Pending Review";
};

const QualityAssurance = () => {
  const { projectId, project, picoExtractions: extractions, projectDocuments: documents, activeStage, handleStageComplete, continueLabel, pageTitle, setChatDocumentIds, setStepStateNote, setMentionProvider } = useProjectDetailContext();
  const { data: allActiveTemplates = [] } = useAllActiveProjectTemplatesWithPrompts(projectId, 'Extraction');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const templateDocumentQueries = useQueries({
    queries: allActiveTemplates.map(t => ({
      queryKey: ["projects", projectId, "templates", t.template.id, "documents"],
      queryFn: () => projectsApi.getTemplateDocuments(projectId, t.template.id),
      enabled: !!projectId && !!t.template.id,
    })),
  });

  // Pending rows are "Select from Evidence Base" picks that haven't been extracted — nothing to review.
  const disabledTabIds = allActiveTemplates
    .filter((_, i) => (withoutPendingSelections(templateDocumentQueries[i]?.data)?.length ?? 0) === 0)
    .map(t => t.template.id);

  const tabStatuses = useMemo(() => {
    const result: Record<string, "pending_review" | "reviewed"> = {};
    allActiveTemplates.forEach((t, i) => {
      const docs = withoutPendingSelections(templateDocumentQueries[i]?.data);
      if (!docs || docs.length === 0) return;
      const allReviewed = docs.every(d => d.status === "Reviewed");
      result[t.template.id] = allReviewed ? "reviewed" : "pending_review";
    });
    return result;
  }, [allActiveTemplates, templateDocumentQueries]);

  const effectiveTemplateId =
    selectedTemplateId ??
    allActiveTemplates.find(t => !disabledTabIds.includes(t.template.id))?.template.id ??
    null;
  const activeTemplate = allActiveTemplates.find(t => t.template.id === effectiveTemplateId) ?? null;
  const templateId = effectiveTemplateId ?? undefined;
  const { data: templateDocumentRows } = useTemplateDocuments(projectId, templateId);
  const persistedDocuments = useMemo(() => withoutPendingSelections(templateDocumentRows), [templateDocumentRows]);
  // Admin > Template Types: with Select from Evidence Base on, review only the documents
  // extracted for this template, not every project document.
  const { extraction_select_from_evidence_base } = useTemplateTypeSettings(project?.projectType);
  const selectFromEvidenceBase = extraction_select_from_evidence_base === true;
  const reviewDocuments = useMemo(
    () => scopeDocumentsToSelection(documents ?? [], buildEvidenceSelectionDocIds(persistedDocuments), selectFromEvidenceBase),
    [documents, persistedDocuments, selectFromEvidenceBase]
  );
  const updateTemplateDocument = useUpdateTemplateDocument();
  const queryClient = useQueryClient();

  const { data: reviewConfig } = useQuery({
    queryKey: ["projects", projectId, "review-config"],
    queryFn: () => projectsApi.getReviewConfig(projectId!),
    enabled: !!projectId,
  });

  const gridRef = useRef<AgGridReact<QARow>>(null);
  const sheetRef = useRef<DataValidationSheetRef>(null);
  const [hasGridColumnFilters, setHasGridColumnFilters] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const handleResetGridFilters = useCallback(() => {
    gridRef.current?.api?.setFilterModel(null);
    setHasGridColumnFilters(false);
  }, []);

  const onFilterChanged = useCallback((event: FilterChangedEvent) => {
    const filterModel = event.api.getFilterModel();
    setHasGridColumnFilters(Object.keys(filterModel).length > 0);
  }, []);
  const [localExtractions, setLocalExtractions] = useState<PicoExtraction[]>(extractions);
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [subgroupDataIdMap, setSubgroupDataIdMap] = useState<Map<string, string>>(new Map());
  const [selectedRow, setSelectedRow] = useState<SelectedRow | null>(null);

  // Scope the chat assistant to whichever document is open in the validation
  // modal; fall back to all project documents when nothing is open.
  useEffect(() => {
    setChatDocumentIds(selectedRow ? [selectedRow.documentId] : null);
  }, [selectedRow, setChatDocumentIds]);
  const currentFieldsDataRef = useRef<Record<string, FieldDataWithAI>>({});
  const { openDocument } = useDocumentViewer();
  const onViewDocument = (doc: Document, page = "", sourceText?: string) => {
    openDocument({ doc, page, sourceText, side: "left", showTabs: true });
  }

  const autoOpenedRef = useRef(false);

  useEffect(() => {
    if (!persistedDocuments || !documents) return;

    const groupedByDocument = new Map<string, TemplateDocumentData[]>();
    persistedDocuments.forEach((templateDoc) => {
      if (!groupedByDocument.has(templateDoc.documentId)) {
        groupedByDocument.set(templateDoc.documentId, []);
      }
      groupedByDocument.get(templateDoc.documentId)!.push(templateDoc);
    });

    const newDataIdMap = new Map<string, string>();
    const loadedExtractions: PicoExtraction[] = [];

    groupedByDocument.forEach((records, documentId) => {
      const document = documents.find((d) => d.id === documentId);
      if (!document) return;

      const subgroupsWithIndex = records
        .map((record) => parseSubgroupFromRecord(record, documentId))
        .filter((item): item is NonNullable<typeof item> => item !== null);

      subgroupsWithIndex.sort((a, b) => a.index - b.index);
      const subgroups = subgroupsWithIndex.map((item) => item.subgroup);

      subgroupsWithIndex.forEach((item) => {
        newDataIdMap.set(`${documentId}-${item.index}`, item.documentDataId);
      });

      const status = records[0]?.status || "Processed";
      const createdAt = records[0]?.createdAt || new Date().toISOString();

      loadedExtractions.push({
        id: `${document.id}-${createdAt}`,
        studyName: document.title,
        authors: document.author,
        countries: [document.country],
        year: document.year,
        sourceType: document.sourceType,
        sourceOrganisation: document.sourceOrganisation,
        studyId: document.id,
        status,
        subgroups,
      });
    });

    if (loadedExtractions.length > 0) {
      setLocalExtractions(loadedExtractions);
    }

    setSubgroupDataIdMap(newDataIdMap);
  }, [persistedDocuments, documents]);

  useEffect(() => {
    if (documents && documents.length > 0) {
      setExpandedIds(new Set(documents.map(doc => doc.id)));
    } else {
      setExpandedIds(new Set(extractions.map(study => study.id)));
    }
  }, [documents, extractions]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCellClicked = (params: CellClickedEvent<QARow>) => {
    const target = params.event?.target as HTMLElement;
    if (target && (target.tagName === 'BUTTON' || target.closest('button'))) {
      return;
    }

    if (params.data?.isParent || !params.colDef?.headerName || params.colDef.field === "studyName") {
      return;
    }

    const fields = params.data?.fields || {};
    const rawFieldsData = params.data?.fieldsData;
    // Build a complete fieldsData — if undefined (new/unextracted rows), construct from fields
    // so handleSaveFieldChanges has a proper base and doesn't drop AI metadata.
    const fieldsData: Record<string, FieldDataWithAI> = rawFieldsData
      ? rawFieldsData
      : Object.fromEntries(
          Object.entries(fields).map(([key, value]) => [
            key,
            { value, status: "Pending" } as FieldDataWithAI,
          ])
        );
    const rowId = params.data?.id || "";
    const lastDashIndex = rowId.lastIndexOf('-');
    const documentId = lastDashIndex > 0 ? rowId.substring(0, lastDashIndex) : rowId;
    const subgroupIndex = lastDashIndex > 0 ? parseInt(rowId.substring(lastDashIndex + 1), 10) : 0;
    const documentDataId = params.data?.documentDataId;

    const document = documents.find(doc => doc.id === documentId);
    const studyName = document?.title || "Study";
    const sourcefile = document?.filename || document?.title || "";

    if (Object.keys(fields).length > 0 && rowId && documentId && sourcefile) {
      const doSwitch = () => {
        currentFieldsDataRef.current = fieldsData;
        setSelectedRow({
          studyName,
          fields,
          fieldsData,
          rowId,
          documentId,
          sourcefile,
          documentDataId,
          subgroupIndex,
        });
        setValidationModalOpen(true);
      };

      if (validationModalOpen && sheetRef.current) {
        sheetRef.current.checkUnsavedChanges(doSwitch);
      } else {
        doSwitch();
      }
    }
  };

  const handleStatusChange = useCallback(async (newStatus: "Pending" | "Reviewed") => {
    if (!selectedRow?.documentDataId || !projectId || !templateId) {
      console.warn("handleStatusChange: missing templateId or documentDataId — status update skipped");
      return;
    }

    const previousStatus = localExtractions.find((e) => e.studyId === selectedRow.documentId)?.status;

    await updateTemplateDocument.mutateAsync({
      projectId,
      templateId,
      documentDataId: selectedRow.documentDataId,
      status: newStatus,
    });

    void auditLogApi.recordAuditEvent({
      projectId,
      entityType: "project_template_document_data",
      entityId: selectedRow.documentDataId,
      entityName: selectedRow.studyName,
      action: newStatus === "Reviewed" ? "mark_reviewed" : "mark_pending",
      stage: "Quality Assurance",
      before: JSON.stringify({ status: previousStatus }),
      after: JSON.stringify({ status: newStatus }),
    });

    const docId = selectedRow.documentId;

    setLocalExtractions((prev) =>
      prev.map((extraction) => {
        if (extraction.studyId !== docId) return extraction;
        return {
          ...extraction,
          status: newStatus,
        };
      })
    );

    queryClient.invalidateQueries({
      queryKey: ["projects", projectId, "templates", templateId, "documents"],
    });
  }, [selectedRow, projectId, templateId, updateTemplateDocument, queryClient, localExtractions]);

  const handleSaveFieldChanges = useCallback(async (
    updatedFields: Record<string, string>,
    approvedFieldName?: string,
    aiScores?: Record<string, number>,
    aiSuggestions?: Record<string, string[]>,
    aiAlternativeAnswers?: Record<string, string>
  ) => {
    if (!selectedRow?.documentDataId || !projectId || !templateId) {
      console.warn("handleSaveFieldChanges: missing templateId or documentDataId — field save skipped");
      return;
    }

    const markAllAsReviewed = approvedFieldName === "*";
    const markAllAsPending = approvedFieldName === "**";
    const updatedFieldsData: Record<string, FieldDataWithAI> = {};

    // Use currentFieldsDataRef as the base for building the save payload.
    // If the ref is empty (e.g. due to an unextracted row on auto-open), fall back to
    // selectedRow.fieldsData so that previously-persisted AI metadata (aiScore,
    // aiSuggestions, aiAlternativeAnswer) for unchanged fields is not silently dropped.
    const baseFieldsData =
      Object.keys(currentFieldsDataRef.current).length > 0
        ? currentFieldsDataRef.current
        : selectedRow.fieldsData ?? {};

    Object.entries(baseFieldsData).forEach(([key, fieldData]) => {
      const { aiAlternativeAnswers: legacyAnswers, ...rest } = fieldData;
      // Migrate legacy plural Record to canonical singular string
      const migratedAnswer = rest.aiAlternativeAnswer ?? legacyAnswers?.[key];
      updatedFieldsData[key] = {
        ...rest,
        ...(migratedAnswer && { aiAlternativeAnswer: migratedAnswer }),
      };
    });

    Object.entries(updatedFields).forEach(([key, value]) => {
      if (updatedFieldsData[key]) {
        const valueChanged = updatedFieldsData[key].value !== value;
        updatedFieldsData[key].value = value;

        if (!approvedFieldName && valueChanged) {
          updatedFieldsData[key].status = "Pending";
        } else {
          updatedFieldsData[key].status = updateFieldStatus(
            updatedFieldsData[key],
            approvedFieldName,
            key,
            markAllAsReviewed,
            markAllAsPending
          );
        }

        if (aiScores?.[key] !== undefined) {
          updatedFieldsData[key].aiScore = aiScores[key];
        }
        if (aiSuggestions?.[key]) {
          updatedFieldsData[key].aiSuggestions = aiSuggestions[key];
        }
        if (aiAlternativeAnswers && key in aiAlternativeAnswers) {
          if (aiAlternativeAnswers[key]) {
            updatedFieldsData[key].aiAlternativeAnswer = aiAlternativeAnswers[key];
          } else {
            // AI no longer suggests an alternative — clear the stale value
            delete updatedFieldsData[key].aiAlternativeAnswer;
          }
        }
      } else {
        let status = "Pending";
        if (markAllAsReviewed || (approvedFieldName && key === approvedFieldName)) {
          status = "Reviewed";
        }

        updatedFieldsData[key] = {
          value,
          status,
          ...(aiScores?.[key] !== undefined && { aiScore: aiScores[key] }),
          ...(aiSuggestions?.[key] && { aiSuggestions: aiSuggestions[key] }),
          ...(aiAlternativeAnswers?.[key] && { aiAlternativeAnswer: aiAlternativeAnswers[key] }),
        };
      }
    });

    const dataPayload = {
      index: selectedRow.subgroupIndex,
      fields: updatedFieldsData,
    };

    await updateTemplateDocument.mutateAsync({
      projectId,
      templateId,
      documentDataId: selectedRow.documentDataId,
      data: JSON.stringify(dataPayload),
    });

    // approvedFieldName encodes the direction of this save:
    //   "*"          -> mark every field Reviewed (whole-document "Reviewed" button)
    //   "**"         -> mark every field Pending (whole-document revert)
    //   "<field>"    -> approve that one field (per-field Approve toggle)
    //   "~<field>"   -> un-approve that one field (per-field Revert toggle)
    //   undefined    -> a plain text edit, not an approval action
    const fieldChangeAction =
      approvedFieldName === "**" || approvedFieldName?.startsWith("~")
        ? "mark_pending"
        : approvedFieldName
          ? "mark_reviewed"
          : "review";

    void auditLogApi.recordAuditEvent({
      projectId,
      entityType: "project_template_document_data",
      entityId: selectedRow.documentDataId,
      entityName: selectedRow.studyName,
      action: fieldChangeAction,
      stage: "Quality Assurance",
      before: JSON.stringify({ fields: baseFieldsData }),
      after: JSON.stringify({ fields: updatedFieldsData }),
    });

    currentFieldsDataRef.current = updatedFieldsData;

    const docId = selectedRow.documentId;
    const sgIndex = selectedRow.subgroupIndex;

    setLocalExtractions((prev) =>
      prev.map((extraction) => {
        if (extraction.studyId !== docId) return extraction;
        return {
          ...extraction,
          subgroups: extraction.subgroups.map((sg, idx) => {
            if (idx !== sgIndex) return sg;
            return { ...sg, fields: updatedFieldsData as unknown as Record<string, string> };
          }),
        };
      })
    );

    queryClient.invalidateQueries({
      queryKey: ["projects", projectId, "templates", templateId, "documents"],
    });
  }, [selectedRow, projectId, templateId, updateTemplateDocument, queryClient]);

  const templateColumns = useMemo(() => {
    if (!activeTemplate?.prompt_libraries?.length) {
      return [];
    }
    return [...activeTemplate.prompt_libraries].sort(
      (a, b) => (a.ordinal ?? Infinity) - (b.ordinal ?? Infinity)
    );
  }, [activeTemplate]);

  const { rowData, allChildRows } = useMemo(() => {
    const rows: QARow[] = [];
    const allChildRows: QARow[] = []; // every subgroup row, regardless of expand state

    const addStudyRows = (
      id: string,
      studyName: string,
      authors: string,
      countries: string,
      studyId: string,
      subgroups: PicoSubgroup[],
      year?: string,
      sourceOrganisation?: string,
      filename?: string
    ) => {
      const hasChildren = subgroups.length > 0;
      const isExpanded = expandedIds.has(id);
      const computedStatus = computeStudyStatus(subgroups);

      rows.push({
        id,
        studyName,
        authors,
        countries,
        year,
        sourceOrganisation,
        filename,
        studyId,
        status: computedStatus,
        isParent: true,
        isExpanded: hasChildren ? isExpanded : undefined
      });

      if (hasChildren) {
        subgroups.forEach((sg, i) => {
          const rowFields: Record<string, string> = {};
          const fieldsData: Record<string, FieldDataWithAI> = {};

          if (sg.fields) {
            Object.entries(sg.fields).forEach(([key, fieldValue]) => {
              const { displayValue, fieldData } = processFieldValue(fieldValue, key);
              rowFields[key] = displayValue;
              fieldsData[key] = fieldData;
            });
          } else {
            const subgroupValues = [sg.population, sg.intervention, sg.comparator, sg.outcome];
            templateColumns.forEach((col, idx) => {
              const value = subgroupValues[idx] ?? "";
              rowFields[col.name] = value;
              fieldsData[col.name] = { value, status: "Pending" };
            });
          }

          const rowKey = `${id}-${i}`;
          const documentDataId = subgroupDataIdMap.get(rowKey);

          const childRow: QARow = {
            id: rowKey,
            studyName: "",
            fields: rowFields,
            fieldsData,
            isParent: false,
            parentId: id,
            documentDataId,
          };

          allChildRows.push(childRow);
          if (isExpanded) rows.push(childRow);
        });
      }
    };

    if (reviewDocuments.length > 0) {
      reviewDocuments.forEach((doc) => {
        const extraction = localExtractions.find((e) => e.studyId === doc.id);
        addStudyRows(
          doc.id,
          doc.title,
          doc.author,
          doc.country,
          doc.id,
          extraction?.subgroups ?? [],
          doc.year,
          doc.sourceOrganisation,
          doc.filename
        );
      });
    } else if (!selectFromEvidenceBase) {
      extractions.forEach((study) => {
        addStudyRows(
          study.id,
          study.studyName,
          study.authors,
          study.countries.join(", "),
          study.studyId,
          study.subgroups,
          study.year,
          study.sourceOrganisation
        );
      });
    }

    return { rowData: rows, allChildRows };
  }, [reviewDocuments, selectFromEvidenceBase, localExtractions, extractions, expandedIds, templateColumns, subgroupDataIdMap]);

  // Plain-text summary of extraction completeness for the current template — fed
  // into the chat assistant's system prompt, independent of expand/collapse state.
  const extractionStateNote = useMemo(() => {
    const documentStatusCounts: Record<string, number> = {};
    let documentCount = 0;
    for (const row of rowData) {
      if (row.isParent) {
        documentCount++;
        documentStatusCounts[row.status] = (documentStatusCounts[row.status] ?? 0) + 1;
      }
    }
    if (documentCount === 0) return null;

    const missingCounts = new Map<string, number>();
    const fieldRowCounts = new Map<string, number>();
    for (const row of allChildRows) {
      if (!row.fields) continue;
      for (const [name, value] of Object.entries(row.fields)) {
        fieldRowCounts.set(name, (fieldRowCounts.get(name) ?? 0) + 1);
        if (!value) missingCounts.set(name, (missingCounts.get(name) ?? 0) + 1);
      }
    }
    const missingFields = Array.from(missingCounts.entries())
      .map(([name, missing]) => ({ name, missing, total: fieldRowCounts.get(name) ?? 0 }))
      .sort((a, b) => b.missing - a.missing)
      .slice(0, 5);

    const statusStr = Object.entries(documentStatusCounts)
      .filter(([, count]) => count > 0)
      .map(([status, count]) => `${count} ${status}`)
      .join(", ");
    let note = `Extraction (${activeTemplate?.template.name ?? "active template"}): ${documentCount} documents` +
      (statusStr ? ` (${statusStr})` : "") + ".";
    if (missingFields.length > 0) {
      note += ` Fields with missing data: ${missingFields.map((f) => `${f.name} (${f.missing}/${f.total} rows)`).join(", ")}.`;
    }

    return note;
  }, [rowData, allChildRows, activeTemplate]);

  // @-mention provider: resolved fresh at send time (see useAssistantThread in
  // @lumanity/ui), so content is always current — never frozen at mention time.
  const mentionProvider = useMemo<MentionProvider>(() => ({
    search: async (query) => {
      const q = query.toLowerCase();
      return documents
        .filter((d) => (d.title || d.filename || "").toLowerCase().includes(q))
        .slice(0, 20)
        .map((d) => ({ id: d.id, label: d.title || d.filename || d.id, description: d.filename }));
    },
    resolveContent: (item) => {
      const parent = rowData.find((r) => r.isParent && r.id === item.id);
      if (!parent) {
        const doc = documents.find((d) => d.id === item.id);
        return `${doc?.title || doc?.filename || item.label} — no data in the current template yet`;
      }
      const lines = [`${parent.studyName || item.label} — Status: ${parent.status ?? "Pending"}`];
      allChildRows
        .filter((r) => r.parentId === item.id)
        .forEach((child, i) => {
          const data = child.fieldsData ?? {};
          const fields = child.fields ?? {};
          const names = new Set([...Object.keys(data), ...Object.keys(fields)]);
          const fieldStrs = Array.from(names).map((name) => {
            const value = (data[name]?.value ?? fields[name] ?? "") || "(empty)";
            const status = data[name]?.status;
            return status ? `${name}: ${value} [${status}]` : `${name}: ${value}`;
          });
          if (fieldStrs.length) lines.push(`Subgroup ${i + 1}: ${fieldStrs.join("; ")}`);
        });
      return lines.join("\n");
    },
  }), [documents, rowData, allChildRows]);

  useEffect(() => {
    setMentionProvider(mentionProvider);
    return () => setMentionProvider(null);
  }, [mentionProvider, setMentionProvider]);

  useEffect(() => {
    setStepStateNote(extractionStateNote);
    return () => setStepStateNote(null);
  }, [extractionStateNote, setStepStateNote]);

  useEffect(() => {
    if (autoOpenedRef.current || rowData.length === 0 || !documents?.length) return;

    const firstChildRow = rowData.find(row => !row.isParent && row.parentId && row.fields && Object.keys(row.fields).length > 0);
    if (!firstChildRow) return;

    const rowId = firstChildRow.id;
    const lastDashIndex = rowId.lastIndexOf('-');
    const documentId = lastDashIndex > 0 ? rowId.substring(0, lastDashIndex) : rowId;
    const subgroupIndex = lastDashIndex > 0 ? parseInt(rowId.substring(lastDashIndex + 1), 10) : 0;
    const document = documents.find(doc => doc.id === documentId);
    const sourcefile = document?.filename || document?.title || "";

    if (!documentId || !sourcefile) return;

    autoOpenedRef.current = true;
    // Build a complete fieldsData ref — if fieldsData is missing (new/unextracted rows),
    // construct entries from fields so that handleSaveFieldChanges has a proper base
    // and doesn't silently drop AI metadata for unchanged fields.
    const initialFieldsData: Record<string, FieldDataWithAI> = firstChildRow.fieldsData
      ? { ...firstChildRow.fieldsData }
      : Object.fromEntries(
          Object.entries(firstChildRow.fields!).map(([key, value]) => [
            key,
            { value, status: "Pending" } as FieldDataWithAI,
          ])
        );
    currentFieldsDataRef.current = initialFieldsData;
    setSelectedRow({
      studyName: document?.title || "Study",
      fields: firstChildRow.fields!,
      fieldsData: initialFieldsData,
      rowId,
      documentId,
      sourcefile,
      documentDataId: firstChildRow.documentDataId,
      subgroupIndex,
    });
    setValidationModalOpen(true);
  }, [rowData, documents]);

  const columnDefs = useMemo<ColDef<QARow>[]>(() => {
    const totalCols = templateColumns.length + 1;
    const studyNameCol: ColDef<QARow> = {
      headerName: "Study Name",
      headerTooltip: "Study Name",
      field: "studyName",
      width: 300,
      minWidth: 300,
      colSpan: (params) => (!params.data?.parentId ? totalCols : 1),
      autoHeight: true,
      wrapText: true,
      cellStyle: (params) => ({
        paddingTop: ".5rem",
        paddingBottom: ".5rem",
        ...(!params.data?.studyName ? {
          // background: "#f7f7f7",
          borderTop: "1px solid #f7f7f7",
          borderBottom: "1px solid #f7f7f7",
        } : {borderLeft: "4px solid #037c72"}),
      }),
      cellRenderer: (params: ICellRendererParams<QARow> & { context: GridContext }) => {
        const row = params.data!;
        return (
          <StudyNameCell
            studyName={row.studyName}
            authors={row.authors}
            countries={row.countries}
            year={row.year}
            sourceOrganisation={row.sourceOrganisation}
            filename={row.filename}
            status={row.status}
            isParent={row.isParent}
            isExpanded={row.isExpanded}
            onToggle={() => params.context.toggleExpand(row.id)}
          />
        );
      },
    };

    const dataCols: ColDef<QARow>[] = templateColumns.map((col) => ({
      headerName: col.name,
      headerTooltip: col.name,
      valueGetter: (params) => params.data?.fields?.[col.name] ?? "",
      flex: 1,
      minWidth: 150,
      autoHeight: true,
      wrapText: true,
      cellStyle: { lineHeight: "inherit", paddingTop: "0.5rem", paddingBottom: "0.5rem" },
      cellRenderer: ExtractedDataCell,
    }));

    return [studyNameCol, ...dataCols];
  }, [templateColumns]);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    resizable: true,
    filter: true,
  }), []);

  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [rowData]);

  const exportRows = usePicoExportRows(localExtractions, templateColumns);
  return (
    <Card className="p-6">
      <div className="flex flex-col-reverse md:flex-row justify-between mb-4">
        <div className="pr-4">
          <h2 className="text-lg font-bold text-foreground">{pageTitle}</h2>
          <p className="text-sm text-muted-foreground">Review and validate extracted data from each study. Click on any cell to open the validation modal.</p>
        </div>

        <div className="flex items-center self-end gap-3 mb-4">
          <ResetFiltersButton hasActiveFilters={hasGridColumnFilters} onReset={handleResetGridFilters} />
          <ExcelExportButton rows={exportRows} sheetName={activeTemplate?.template.name ?? "QA Report"} fileName={`${toSafeFileName(project.name)}-qa-report.xlsx`} disabled={!exportRows.length} />
          {project.projectType?.toUpperCase() !== "GVD" && (
            <Button
              className="bg-brand-teal text-white hover:bg-brand-teal/90 gap-2"
              onClick={() => handleStageComplete(activeStage)}
            >
              <FileText className="w-4 h-4" /> {continueLabel}
            </Button>
          )}
        </div>
      </div>

      <div className="mb-4">
        <TemplateTabStrip
          templates={allActiveTemplates.map(t => t.template)}
          selectedId={effectiveTemplateId ?? ""}
          disabledIds={disabledTabIds}
          statuses={tabStatuses}
          onSelect={(id) => {
            const doSwitch = () => {
              setLocalExtractions([]);
              setSubgroupDataIdMap(new Map());
              setValidationModalOpen(false);
              setSelectedRow(null);
              autoOpenedRef.current = false;
              setSelectedTemplateId(id);
            };

            if (validationModalOpen && sheetRef.current) {
              sheetRef.current.checkUnsavedChanges(doSwitch);
            } else {
              doSwitch();
            }
          }}
        />
      </div>

      <Alert variant="info" dismissable className="mb-4">
        <AlertDescription><span className="font-semibold">💡 Tip:</span> Click any cell to open the Data Validation modal where you can view citations and validate data</AlertDescription>
      </Alert>

      <div className="ag-theme-alpine border overflow-hidden mb-8" style={{ height: 500 }}>
        <AgGridReact<QARow>
          theme="legacy"
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          context={{ toggleExpand, documents, onViewDocument } satisfies GridContext}
          onCellClicked={handleCellClicked}
          animateRows={true}
          headerHeight={44}
          getRowId={(params) => params.data.id}
          onFilterChanged={onFilterChanged}
        />
      </div>

      <Alert variant="warning" className="text-forground">
        <FileText className="w-4 h-4 text-black" />
        <AlertTitle><span className="font-semibold">Quality Standards</span></AlertTitle>
        <AlertDescription>Ensure all project deliverables meet quality standards and client requirements before final submission.</AlertDescription>
      </Alert>

      {selectedRow && (
        <DataValidationSheet
          ref={sheetRef}
          open={validationModalOpen}
          onClose={() => {
            setValidationModalOpen(false);
            setSelectedRow(null);
          }}
          studyName={selectedRow.studyName}
          fields={selectedRow.fields}
          fieldsData={selectedRow.fieldsData}
          documentId={selectedRow.documentId}
          sourcefile={selectedRow.sourcefile}
          templateColumns={templateColumns}
          documents={documents}
          reviewApiName={reviewConfig?.api_name ?? undefined}
          model={project.model}
          onStatusChange={handleStatusChange}
          onSave={handleSaveFieldChanges}
        />
      )}

    </Card>
  );
};

export default QualityAssurance;
